/**
 * Order Service
 * Service for managing order operations via Commerce Service API
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-004
 */

import type {
  OrderDetails,
  OrderSummary,
  OrderFilters,
  ApiResponse,
  PagedResponse,
} from '@/types/order';

// ============================================
// CONFIGURATION
// ============================================

const COMMERCE_SERVICE_BASE_URL =
  import.meta.env.VITE_COMMERCE_SERVICE_URL || 'http://localhost:8083/commerce-service';
const ORDER_API_PATH = '/api/v1/orders';

/**
 * Get JWT token from localStorage
 */
function getAuthToken(): string | null {
  const token = localStorage.getItem('saubhagya_jwt_token');
  console.log('[OrderService] JWT token check:', token ? 'Token exists' : 'No token found');
  return token;
}

/**
 * Validate JWT token exists
 */
function validateAuthToken(): void {
  const token = getAuthToken();
  if (!token) {
    throw new OrderApiError(
      'Authentication required. Please login again.',
      401
    );
  }
}

/**
 * Get authorization headers
 */
function getHeaders(): HeadersInit {
  const token = getAuthToken();
  // Token may already include "Bearer " prefix, so check before adding it
  const authHeader = token
    ? token.startsWith('Bearer ') ? token : `Bearer ${token}`
    : undefined;
  return {
    'Content-Type': 'application/json',
    ...(authHeader && { Authorization: authHeader }),
  };
}

// ============================================
// ERROR HANDLING
// ============================================

/**
 * Error codes for specific error types
 */
export enum OrderErrorCode {
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  TIMEOUT = 'TIMEOUT',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Custom error class for API errors
 */
export class OrderApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public errorCode?: OrderErrorCode,
    public errors?: string[]
  ) {
    super(message);
    this.name = 'OrderApiError';
  }
}

/**
 * Parse error response from API
 */
async function parseErrorResponse(response: Response): Promise<string> {
  try {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const errorData = await response.json();

      if (errorData.message) {
        return errorData.message;
      }
      if (errorData.error) {
        return errorData.error;
      }
      if (errorData.errors && Array.isArray(errorData.errors)) {
        return errorData.errors.join(', ');
      }
    }

    return response.statusText || 'An error occurred';
  } catch {
    return 'An unexpected error occurred';
  }
}

/**
 * Handle API response and errors
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorMessage = await parseErrorResponse(response);

    let userMessage = errorMessage;
    let errorCode = OrderErrorCode.UNKNOWN;

    switch (response.status) {
      case 400:
        userMessage = `Validation Error: ${errorMessage}`;
        errorCode = OrderErrorCode.VALIDATION_ERROR;
        break;
      case 401:
        userMessage = 'Authentication required. Please login again.';
        errorCode = OrderErrorCode.UNAUTHORIZED;
        break;
      case 403:
        userMessage = 'You do not have permission to perform this action.';
        errorCode = OrderErrorCode.FORBIDDEN;
        break;
      case 404:
        userMessage = 'Order not found.';
        errorCode = OrderErrorCode.NOT_FOUND;
        break;
      case 409:
        userMessage = `Conflict: ${errorMessage}`;
        break;
      case 500:
        userMessage = 'Server error. Please try again later.';
        errorCode = OrderErrorCode.SERVER_ERROR;
        break;
      case 503:
        userMessage = 'Service unavailable. Please try again later.';
        errorCode = OrderErrorCode.SERVICE_UNAVAILABLE;
        break;
    }

    console.error('[OrderService] HTTP Error:', {
      status: response.status,
      errorCode,
      message: userMessage,
    });

    throw new OrderApiError(userMessage, response.status, errorCode);
  }

  try {
    return await response.json();
  } catch (parseError) {
    console.error('[OrderService] JSON parse error:', parseError);
    throw new OrderApiError('Invalid response format from server', 500, OrderErrorCode.SERVER_ERROR);
  }
}

// ============================================
// DEMO DATA HELPERS
// ============================================

/**
 * Generate demo order details for testing when backend is unavailable
 */
function generateDemoOrderDetails(orderId: number): OrderDetails {
  return {
    id: orderId,
    orderNumber: `ORD-2024-${String(orderId).padStart(5, '0')}`,
    orderDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    orderStatus: 'CONFIRMED',
    customer: {
      customerId: 1001,
      customerName: 'Demo Customer Pvt Ltd',
      email: 'customer@demo.com',
      phone: '+91-9876543210',
      gstNumber: '27AABCU9603R1ZX',
      panNumber: 'AABCU9603R',
    },
    contract: {
      contractId: 501,
      contractNumber: 'CNT-2024-001',
      contractDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
      contractStatus: 'ACTIVE',
      contractUrl: undefined,
    },
    gstBreakdown: {
      subtotal: 50000,
      totalDiscount: 2500,
      cgstAmount: 4275,
      sgstAmount: 4275,
      igstAmount: 0,
      totalTax: 8550,
      totalAmount: 56050,
    },
    lineItems: [
      {
        id: 1,
        orderLineNumber: 'LINE-001',
        productType: 'BIO_CNG',
        productName: 'Bio-CNG Grade A',
        description: 'High purity Bio-CNG with 95%+ methane content',
        quantity: 1000,
        quantityUnit: 'KG',
        unitPrice: 45,
        discountPercentage: 5,
        discountAmount: 2250,
        taxBreakdown: [
          { taxType: 'CGST', rate: 9, amount: 3823.5 },
          { taxType: 'SGST', rate: 9, amount: 3823.5 },
        ],
        totalTax: 7647,
        lineTotal: 50397,
        batchAllocationIds: [101, 102],
      },
      {
        id: 2,
        orderLineNumber: 'LINE-002',
        productType: 'SLURRY',
        productName: 'Organic Slurry Premium',
        description: 'Nutrient-rich organic slurry for fertilization',
        quantity: 500,
        quantityUnit: 'LITERS',
        unitPrice: 5,
        discountPercentage: 10,
        discountAmount: 250,
        taxBreakdown: [
          { taxType: 'CGST', rate: 9, amount: 202.5 },
          { taxType: 'SGST', rate: 9, amount: 202.5 },
        ],
        totalTax: 405,
        lineTotal: 2655,
        batchAllocationIds: [201],
      },
    ],
    delivery: {
      address: {
        addressLine1: '123 Industrial Area',
        addressLine2: 'Sector 15',
        city: 'Mumbai',
        state: 'Maharashtra',
        pincode: '400001',
        country: 'India',
        landmark: 'Near Highway Junction',
      },
      contactPerson: 'Rajesh Kumar',
      contactPhone: '+91-9123456789',
      expectedDeliveryDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0],
      specialInstructions: 'Handle with care. Deliver during business hours only (9 AM - 6 PM)',
    },
    statusTimeline: [
      {
        status: 'PENDING',
        timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        updatedBy: 'System',
        remarks: 'Order created',
      },
      {
        status: 'CONFIRMED',
        timestamp: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
        updatedBy: 'Sales Team',
        remarks: 'Order confirmed after payment verification',
      },
    ],
    invoices: [
      {
        invoiceId: '2001',
        invoiceNumber: 'INV-2024-001234',
        invoiceDate: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        invoiceAmount: 56050,
        status: 'GENERATED',
      },
    ],
    batchAllocations: [
      {
        allocationId: 1,
        batchId: '101',
        batchNumber: 'BATCH-CNG-2024-101',
        allocatedQuantityKg: 600,
        allocatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        qualityGrade: 'GRADE_A',
        productionDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0],
      },
      {
        allocationId: 2,
        batchId: '102',
        batchNumber: 'BATCH-CNG-2024-102',
        allocatedQuantityKg: 400,
        allocatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        qualityGrade: 'GRADE_A',
        productionDate: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0],
      },
      {
        allocationId: 3,
        batchId: '201',
        batchNumber: 'BATCH-SLURRY-2024-201',
        allocatedQuantityKg: 500,
        allocatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        qualityGrade: 'STANDARD',
        productionDate: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0],
      },
    ],
    payment: {
      paymentStatus: 'PARTIAL',
      totalAmount: 56050,
      paidAmount: 30000,
      balanceDue: 26050,
      lastPaymentDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
      nextDueDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0],
    },
    availableActions: ['VIEW_INVOICE', 'SCHEDULE_DELIVERY', 'CANCEL_ORDER', 'EDIT_ORDER'],
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'Sales Admin',
    updatedAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    updatedBy: 'Sales Team',
    remarks: 'Priority customer - handle with care',
    internalNotes: 'Customer has requested eco-friendly packaging',
  };
}

// ============================================
// BACKEND RESPONSE MAPPER
// ============================================

/**
 * Backend DTO structure (from OrderDetailsDTO.java)
 */
interface BackendOrderDetailsDTO {
  orderHeader: {
    orderId: number;
    orderNumber: string;
    orderDate: string;
    status: OrderStatus;
    customerId: string;
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    customerGstin?: string;
    contractNumber?: string;
    contractId?: string;
    totalAmount: number;
    gstAmount: number;
    cgstAmount?: number;
    sgstAmount?: number;
    igstAmount?: number;
    finalAmount: number;
    currency?: string;
    qualityGrade?: string;
    quantityCubicMeters?: number;
    unitPrice?: number;
  };
  lineItems: Array<{
    lineItemId: number;
    productName: string;
    batchNumber?: string;
    quantity: number;
    unit: string;
    unitPrice: number;
    discount: number;
    discountAmount: number;
    taxDetails?: {
      cgstRate: number;
      cgstAmount: number;
      sgstRate: number;
      sgstAmount: number;
      igstRate: number;
      igstAmount: number;
      totalTax: number;
    };
    lineTotal: number;
    deliveryStatus?: string;
    deliveredQuantity?: number;
    remainingQuantity?: number;
  }>;
  deliveryInfo?: {
    deliveryAddress: string;
    contactPerson: string;
    contactPhone: string;
    requestedDeliveryDate?: string;
    confirmedDeliveryDate?: string;
    actualDeliveryDate?: string;
    specialInstructions?: string;
    deliveryMode?: string;
    currentLocation?: string;
    deliveryStatus?: string;
  };
  statusTimeline?: {
    events: Array<{
      status: OrderStatus;
      statusLabel: string;
      timestamp: string;
      performedBy: string;
      remarks?: string;
      isCompleted: boolean;
      isCurrent: boolean;
    }>;
    currentStatus: string;
    completionPercentage: number;
  };
  associatedRecords?: {
    invoices?: Array<{
      invoiceId: string;
      invoiceNumber: string;
      invoiceDate: string;
      dueDate?: string;
      invoiceAmount: number;
      status: string;
    }>;
    deliveries?: Array<{
      deliveryId: string;
      deliveryNumber: string;
      scheduledDate?: string;
      actualDeliveryDate?: string;
      status: string;
      vehicleNumber?: string;
      driverName?: string;
      driverPhone?: string;
      deliveredQuantity?: number;
    }>;
    batchAllocations?: Array<{
      allocationId: number;
      batchNumber: string;
      batchId: string;
      allocatedQuantityKg: number;
      allocatedAt: string;
      qualityGrade?: string;
      productionDate?: string;
    }>;
    paymentInfo?: {
      paymentStatus: string;
      paymentTerms?: string;
      totalAmount: number;
      paidAmount: number;
      balanceDue: number;
      paymentCompletionPercentage?: number;
      paymentDueDate?: string;
      transactions?: Array<{
        transactionId: string;
        transactionDate: string;
        amount: number;
        paymentMethod: string;
        referenceNumber: string;
        status: string;
      }>;
    };
  };
  availableActions?: string[];
}

/**
 * Map backend OrderDetailsDTO to frontend OrderDetails
 */
function mapBackendOrderDetails(backend: BackendOrderDetailsDTO): OrderDetails {
  const header = backend.orderHeader;
  const timeline = backend.statusTimeline?.events || [];

  // Enhanced logging for GST data
  console.log('[OrderService] Mapping backend order:', {
    orderId: header.orderId,
    orderNumber: header.orderNumber,
    totalAmount: header.totalAmount,
    gstAmount: header.gstAmount,
    finalAmount: header.finalAmount,
    hasDeliveryInfo: !!backend.deliveryInfo,
    actualDeliveryDate: backend.deliveryInfo?.actualDeliveryDate,
  });

  // Calculate GST breakdown with proper handling
  const cgstAmount = header.cgstAmount || (header.gstAmount / 2);
  const sgstAmount = header.sgstAmount || (header.gstAmount / 2);
  const igstAmount = header.igstAmount || 0;

  return {
    id: header.orderId,
    orderNumber: header.orderNumber,
    orderDate: header.orderDate,
    orderStatus: header.status,

    customer: {
      customerId: parseInt(header.customerId) || 0,
      customerName: header.customerName,
      email: header.customerEmail,
      phone: header.customerPhone,
      gstNumber: header.customerGstin,
    },

    contract: header.contractNumber ? {
      contractId: parseInt(header.contractId || '0'),
      contractNumber: header.contractNumber,
      contractDate: '',
      contractStatus: 'ACTIVE',
    } : undefined,

    gstBreakdown: {
      subtotal: header.totalAmount - header.gstAmount,
      totalDiscount: 0,
      cgstAmount,
      sgstAmount,
      igstAmount,
      totalTax: header.gstAmount,
      totalAmount: header.finalAmount,
    },

    lineItems: backend.lineItems.map((item) => ({
      id: item.lineItemId,
      orderLineNumber: `LINE-${item.lineItemId}`,
      productType: 'BIO_CNG',
      productName: item.productName,
      quantity: item.quantity,
      quantityUnit: item.unit as any,
      unitPrice: item.unitPrice,
      discountPercentage: item.discount,
      discountAmount: item.discountAmount,
      taxBreakdown: item.taxDetails ? [
        { taxType: 'CGST' as const, rate: item.taxDetails.cgstRate, amount: item.taxDetails.cgstAmount },
        { taxType: 'SGST' as const, rate: item.taxDetails.sgstRate, amount: item.taxDetails.sgstAmount },
      ] : [],
      totalTax: item.taxDetails?.totalTax || 0,
      lineTotal: item.lineTotal,
    })),

    delivery: backend.deliveryInfo ? {
      address: {
        addressLine1: backend.deliveryInfo.deliveryAddress,
        city: '',
        state: '',
        pincode: '',
        country: 'India',
      },
      contactPerson: backend.deliveryInfo.contactPerson,
      contactPhone: backend.deliveryInfo.contactPhone,
      expectedDeliveryDate: backend.deliveryInfo.confirmedDeliveryDate || '',
      actualDeliveryDate: backend.deliveryInfo.actualDeliveryDate || undefined,
      specialInstructions: backend.deliveryInfo.specialInstructions,
    } : {
      address: {
        addressLine1: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India',
      },
      contactPerson: '',
      contactPhone: '',
      expectedDeliveryDate: '',
    },

    statusTimeline: timeline.map((event) => ({
      status: event.status,
      timestamp: event.timestamp,
      updatedBy: event.performedBy,
      remarks: event.remarks,
    })),

    invoices: backend.associatedRecords?.invoices,
    deliveries: backend.associatedRecords?.deliveries,
    batchAllocations: backend.associatedRecords?.batchAllocations || [],

    payment: backend.associatedRecords?.paymentInfo ? {
      paymentStatus: backend.associatedRecords.paymentInfo.paymentStatus as any,
      totalAmount: backend.associatedRecords.paymentInfo.totalAmount,
      paidAmount: backend.associatedRecords.paymentInfo.paidAmount,
      balanceDue: backend.associatedRecords.paymentInfo.balanceDue,
      lastPaymentDate: backend.associatedRecords.paymentInfo.transactions?.[0]?.transactionDate,
      nextDueDate: backend.associatedRecords.paymentInfo.paymentDueDate,
    } : {
      paymentStatus: 'PENDING',
      totalAmount: header.finalAmount,
      paidAmount: 0,
      balanceDue: header.finalAmount,
    },

    availableActions: backend.availableActions,

    createdAt: header.orderDate,
    createdBy: 'System',
    updatedAt: header.orderDate,
    updatedBy: 'System',
  };
}

// ============================================
// API SERVICE METHODS
// ============================================

/**
 * Order Service
 */
export const orderService = {
  /**
   * Get order by ID
   *
   * @param id - Order ID
   * @returns Order details
   * @throws OrderApiError if fetch fails
   */
  async getOrderById(id: number): Promise<OrderDetails> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${id}`;
      console.log(`[OrderService] Fetching order ${id} from:`, url);

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });

      console.log(`[OrderService] Response status for order ${id}:`, response.status);

      const backendData = await handleResponse<BackendOrderDetailsDTO>(response);

      // Map backend DTO to frontend OrderDetails
      const mappedOrder = mapBackendOrderDetails(backendData);
      console.log('[OrderService] Successfully mapped order:', mappedOrder.orderNumber);

      return mappedOrder;
    } catch (error) {
      console.error('[OrderService] Error fetching order:', error);

      if (error instanceof OrderApiError) {
        throw error;
      }

      // Handle network errors - provide demo mode
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.warn(
          'Backend API unavailable. Using demo mode for order details.'
        );
        return generateDemoOrderDetails(id);
      }

      throw new OrderApiError('Failed to fetch order details.');
    }
  },

  /**
   * Get all orders with optional filters
   *
   * @param page - Page number (0-indexed)
   * @param size - Page size
   * @param filters - Optional filters (status, customer, dates, search)
   * @returns Paginated order summaries
   * @throws OrderApiError if fetch fails
   */
  async getAllOrders(
    page: number = 0,
    size: number = 20,
    filters?: OrderFilters
  ): Promise<PagedResponse<OrderSummary>> {
    console.log('[OrderService] ========== getAllOrders START ==========');
    console.log('[OrderService] Request params:', { page, size, filters });
    console.log('[OrderService] Config:', {
      baseUrl: COMMERCE_SERVICE_BASE_URL,
      apiPath: ORDER_API_PATH,
    });

    // IMPORTANT: Check token availability (informational only, not blocking)
    const token = getAuthToken();
    console.log('[OrderService] Token status:', {
      tokenExists: !!token,
      tokenLength: token?.length || 0,
      tokenPrefix: token ? `${token.substring(0, 20)}...` : 'NONE',
    });

    try {
      // NOTE: We do NOT validate/throw here - let the API endpoint handle 401 responses
      // This allows the request to proceed and proper error handling downstream

      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('size', size.toString());

      if (filters) {
        if (filters.status) queryParams.append('status', filters.status);
        if (filters.customerId) queryParams.append('customerId', filters.customerId.toString());
        if (filters.fromDate) queryParams.append('fromDate', filters.fromDate);
        if (filters.toDate) queryParams.append('toDate', filters.toDate);
        if (filters.searchTerm) queryParams.append('search', filters.searchTerm);
      }

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}?${queryParams.toString()}`;
      console.log('[OrderService] Full request URL:', url);
      console.log('[OrderService] Query params:', queryParams.toString());

      const headers = getHeaders();
      console.log('[OrderService] Request headers:', {
        'Content-Type': headers['Content-Type'],
        'Authorization': headers['Authorization'] ? 'Bearer [TOKEN_PRESENT]' : 'MISSING',
      });

      console.log('[OrderService] Initiating fetch request...');
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      console.log('[OrderService] Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: {
          contentType: response.headers.get('content-type'),
        },
      });

      const data = await handleResponse<PagedResponse<OrderSummary>>(response);
      console.log('[OrderService] ✅ Successfully fetched orders:', {
        totalElements: data.totalElements,
        totalPages: data.totalPages,
        currentPage: data.currentPage,
        contentLength: data.content.length,
      });
      console.log('[OrderService] ========== getAllOrders END (SUCCESS) ==========');

      return data;
    } catch (error) {
      console.error('[OrderService] ========== getAllOrders END (ERROR) ==========');
      console.error('[OrderService] ❌ Error in getAllOrders:', error);
      console.error('[OrderService] Error type:', error?.constructor?.name);
      console.error('[OrderService] Error stack:', error instanceof Error ? error.stack : 'No stack trace');

      if (error instanceof OrderApiError) {
        console.error('[OrderService] OrderApiError details:', {
          message: error.message,
          statusCode: error.statusCode,
          errorCode: error.errorCode,
        });
        throw error;
      }

      // Handle network errors (service not running or unreachable)
      if (error instanceof TypeError) {
        const networkErrorMsg = `Network error: Cannot reach commerce service at ${COMMERCE_SERVICE_BASE_URL}.\n\nPlease ensure:\n1. Commerce service is running on port 8083\n2. Service URL is correct in .env file (VITE_COMMERCE_SERVICE_URL)\n3. No CORS issues blocking the request\n4. JWT token is valid and not expired`;
        console.error('[OrderService] Network error details:', networkErrorMsg);
        throw new OrderApiError(
          networkErrorMsg,
          0,
          OrderErrorCode.NETWORK_ERROR
        );
      }

      // Handle timeout errors
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new OrderApiError(
          'Request timeout. The commerce service took too long to respond.',
          0,
          OrderErrorCode.TIMEOUT
        );
      }

      // Generic error
      console.error('[OrderService] Unknown error - treating as generic failure');
      throw new OrderApiError(
        'Failed to fetch orders. Please try again or contact support.',
        500,
        OrderErrorCode.UNKNOWN
      );
    }
  },

  /**
   * Create a new order
   *
   * @param orderData - Order creation data (contractId, customerId, quantity, deliveryDate, notes)
   * @returns Created order details
   * @throws OrderApiError if creation fails
   */
  async createOrder(orderData: {
    contractId: number;
    customerId: number;
    quantity: number;
    requestedDeliveryDate: string;
    notes?: string;
  }): Promise<OrderDetails> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}`;
      console.log('[OrderService] Creating order:', url);
      console.log('[OrderService] Order data:', orderData);

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(orderData),
      });

      console.log('[OrderService] Create response status:', response.status);

      const backendData = await handleResponse<BackendOrderDetailsDTO>(response);
      const mappedOrder = mapBackendOrderDetails(backendData);

      console.log('[OrderService] Order created successfully:', mappedOrder.orderNumber);
      return mappedOrder;
    } catch (error) {
      console.error('[OrderService] Error creating order:', error);

      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to create order.');
    }
  },

  /**
   * Update an existing order
   *
   * @param id - Order ID
   * @param updates - Partial order data to update
   * @returns Updated order details
   * @throws OrderApiError if update fails
   */
  async updateOrder(
    id: number,
    updates: {
      quantity?: number;
      requestedDeliveryDate?: string;
      notes?: string;
      status?: string;
    }
  ): Promise<OrderDetails> {
    try {
      validateAuthToken();

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${id}`;
      console.log('[OrderService] Updating order:', url);
      console.log('[OrderService] Update data:', updates);

      const response = await fetch(url, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify(updates),
      });

      console.log('[OrderService] Update response status:', response.status);

      const backendData = await handleResponse<BackendOrderDetailsDTO>(response);
      const mappedOrder = mapBackendOrderDetails(backendData);

      console.log('[OrderService] Order updated successfully:', mappedOrder.orderNumber);
      return mappedOrder;
    } catch (error) {
      console.error('[OrderService] Error updating order:', error);

      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to update order.');
    }
  },

  /**
   * Confirm an order (PENDING → CONFIRMED)
   *
   * @param id - Order ID
   * @param remarks - Optional confirmation remarks
   * @returns Updated order details
   * @throws OrderApiError if action fails
   */
  async confirmOrder(id: number, remarks?: string): Promise<OrderDetails> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${id}/confirm`;

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ remarks }),
      });

      return await handleResponse<OrderDetails>(response);
    } catch (error) {
      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to confirm order.');
    }
  },

  /**
   * Cancel an order
   *
   * @param id - Order ID
   * @param reason - Cancellation reason
   * @returns Updated order details
   * @throws OrderApiError if action fails
   */
  async cancelOrder(id: number, reason: string): Promise<OrderDetails> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${id}/cancel`;

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ reason }),
      });

      return await handleResponse<OrderDetails>(response);
    } catch (error) {
      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to cancel order.');
    }
  },

  /**
   * Mark order as ready for dispatch
   *
   * @param id - Order ID
   * @returns Updated order details
   * @throws OrderApiError if action fails
   */
  async markReady(id: number): Promise<OrderDetails> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${id}/ready`;

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
      });

      return await handleResponse<OrderDetails>(response);
    } catch (error) {
      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to mark order as ready.');
    }
  },

  /**
   * Schedule delivery for an order
   *
   * @param id - Order ID
   * @param deliveryDate - Scheduled delivery date
   * @returns Updated order details
   * @throws OrderApiError if action fails
   */
  async scheduleDelivery(
    id: number,
    deliveryDate: string
  ): Promise<OrderDetails> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${id}/schedule-delivery`;

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ deliveryDate }),
      });

      return await handleResponse<OrderDetails>(response);
    } catch (error) {
      if (error instanceof OrderApiError) {
        throw error;
      }

      throw new OrderApiError('Failed to schedule delivery.');
    }
  },

  /**
   * Check if service is available
   *
   * @returns true if service is available
   */
  async checkHealth(): Promise<boolean> {
    try {
      const response = await fetch(
        `${COMMERCE_SERVICE_BASE_URL}/actuator/health`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      return response.ok;
    } catch {
      return false;
    }
  },
};

export default orderService;
